import { useState } from "react";
import { useNavigate } from "react-router";
import { postJson } from "../api";
import { formatText, useTexts } from "../texts";
import { Notice, TextField, useToast } from "../ui/kit";
import { PasswordField } from "../ui/pieces";
import { errorText, type AiError } from "./ai";
import { Window } from "./widgets";
import styles from "./ai.module.css";

/**
 * Ventana «Agregar API key» (C30 con pasos): API key · Probar · Modelos. Siempre se abre desde un proveedor y ya sabe
 * cuál es: no hay «conectar proveedor» (decisión del dueño, 2026-10-30). La key se prueba antes de guardarla
 * (test-candidate) y vive solo en memoria mientras la ventana está abierta; nunca en la dirección. El nombre es lo que
 * se ve al elegir la API key de una ruta: opcional en la primera («Principal»), obligatorio y distinto en las demás.
 */
export function ApiKeyWindow({
  open,
  provider,
  firstKey,
  onClose,
}: {
  open: boolean;
  provider: { code: string; name: string };
  firstKey: boolean;
  onClose: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; field?: string | null } | null>(null);
  const [found, setFound] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  // Al abrir vuelve al primer paso; al cerrar olvida la key.
  if (open !== opened) {
    setOpened(open);
    setStep(0);
    setName("");
    setKey("");
    setError(null);
    setWarning(null);
  }

  const steps = [L("ADM_AI_STEP_KEY"), L("ADM_AI_STEP_TEST"), L("ADM_AI_STEP_MODELS")];
  const test = async () => {
    if (busy) return;
    if (!firstKey && !name.trim()) {
      setError({ text: L("ADM_AI_KEY_NEED_NAME"), field: "friendlyName" });
      return;
    }
    if (!key.trim()) {
      setError({ text: L("CONNECT_NEED_KEY"), field: "apiKey" });
      return;
    }
    setBusy(true);
    setError(null);
    const reply = await postJson<{ ok?: boolean; models?: number } & AiError>("/api/admin/ai/connect/test", {
      providerCode: provider.code,
      apiKey: key,
    });
    setBusy(false);
    if (reply.status === 200 && reply.body?.ok) {
      setFound(reply.body.models ?? 0);
      setStep(1);
    } else setError({ text: errorText(L, reply.body), field: reply.body?.field });
  };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const reply = await postJson<{ connected?: boolean; models?: number; warningKey?: string } & AiError>(
      "/api/admin/ai/connect",
      { providerCode: provider.code, apiKey: key, friendlyName: name },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.connected) {
      setKey(""); // Guardada cifrada: la key ya no queda en la pantalla.
      setFound(reply.body.models ?? found);
      setWarning(reply.body.warningKey ? L(reply.body.warningKey) : null);
      setStep(2);
      return;
    }
    setError({ text: errorText(L, reply.body), field: reply.body?.field });
    if (reply.body?.field) setStep(0);
  };
  const finish = (href?: string) => {
    toast.show(formatText(L("ADM_AI_KEY_ADDED_TOAST"), provider.name));
    onClose();
    if (href) navigate(href);
  };

  const footer =
    step === 0 ? (
      <>
        <button type="button" className="tm-btn secondary" onClick={onClose}>
          {L("ACTION_CANCEL")}
        </button>
        <button
          type="button"
          className="tm-btn primary"
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={() => void test()}
        >
          {busy ? L("NPA_AI_TESTING") : L("ADM_AI_TEST")}
        </button>
      </>
    ) : step === 1 ? (
      <>
        <button type="button" className="tm-btn secondary" disabled={busy} onClick={() => setStep(0)}>
          {L("ADM_AI_BACK")}
        </button>
        <button
          type="button"
          className="tm-btn primary"
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={() => void save()}
        >
          {busy ? L("NPA_AI_SAVING") : L("ADM_AI_SAVE_AND_SYNC")}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="tm-btn secondary" onClick={() => finish("/Ai/Rutas?route=TEXT_CHAT")}>
          {L("ADM_AI_GO_ROUTES")}
        </button>
        <button type="button" className="tm-btn primary" onClick={() => finish()}>
          {L("ADM_AI_DONE")}
        </button>
      </>
    );

  return (
    <Window
      open={open}
      title={formatText(L("ADM_AI_ADD_KEY_TITLE"), provider.name)}
      onClose={step === 2 ? () => finish() : onClose}
      wide
      steps={{ labels: steps, current: step }}
      footer={footer}
    >
      {step === 0 ? (
        <form
          className={styles.windowBlock}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void test();
          }}
        >
          {error && !error.field ? <Notice tone="error">{error.text}</Notice> : null}
          <TextField
            id="api-key-name"
            label={L("ADM_AI_KEY_NAME")}
            optional={firstKey ? L("CFG1_R1_OPTIONAL") : undefined}
            value={name}
            maxLength={100}
            placeholder={firstKey ? L("ADM_AI_KEY_NAME_FIRST") : L("ADM_AI_KEY_NAME_HINT")}
            help={L("ADM_AI_KEY_NAME_HELP")}
            error={error?.field === "friendlyName" ? error.text : null}
            onChange={(value) => {
              setName(value);
              if (error?.field === "friendlyName" && value.trim()) setError(null);
            }}
          />
          <PasswordField
            id="api-key-value"
            label={L("ADM_AI_ACCESS_KEY")}
            value={key}
            onChange={(value) => {
              setKey(value);
              if (error?.field === "apiKey" && value.trim()) setError(null);
            }}
            autoComplete="new-password"
            error={error?.field === "apiKey" ? error.text : null}
            help={L("ADM_AI_KEY_STORED")}
          />
        </form>
      ) : step === 1 ? (
        <div className={styles.windowBlock}>
          {error ? <Notice tone="error">{error.text}</Notice> : null}
          <Notice tone="done">{formatText(L("ADM_AI_KEY_WORKS"), found.toLocaleString(locale))}</Notice>
        </div>
      ) : (
        <div className={styles.windowBlock}>
          <Notice tone="done">{formatText(L("ADM_AI_CONNECTION_VERIFIED"), found.toLocaleString(locale))}</Notice>
          {warning ? <Notice tone="warning">{warning}</Notice> : null}
        </div>
      )}
    </Window>
  );
}
